/**
 * "Far-off" jobs on the EMT job board (feature flag `emt_readiness_v2`). Pure: no Firebase.
 *
 * THE rule: a job is far off for an EMT when it REQUIRES a credential level on the EMS ladder
 * (EMT-B < AEMT < Paramedic, NREMT-B/I/P counted at the same levels) that is HIGHER than every level
 * the EMT holds or has pending on their profile. Example: a Paramedic job for an EMT-Basic.
 * Far-off jobs are hidden by default behind a visible "Show all jobs (N hidden)" toggle.
 *
 * Never hidden (fail-open):
 *  - a job with no required ladder level (only BLS, EVOC, a State EMS Certification, or nothing);
 *  - for an EMT with no credentials, or with no ladder-level credential at all (only BLS, say):
 *    we cannot tell how far off anything is, so they see every job;
 *  - "pending" counts as held: a credential on the profile whose document is still being reviewed
 *    still sets the EMT's level, because that EMT is close, not far.
 *
 * Levels come from the existing ladder helpers: certificationSatisfies (the CERT_LEVEL_RANK ladder),
 * and certificationSatisfiesForJob so a State EMS Certification in a state that certifies without
 * NREMT (Florida) counts at its recorded level exactly as the job page's apply gate counts it. A
 * State EMS Certification anywhere else counts at its recorded level (EMT-B when none is recorded).
 * Only required certifications count: preferred ones never gate applying, so they never hide a job.
 *
 * Flag `driver_roles` (2026-10-09), passed in as options; off = exactly the rule above:
 *  - EMR joins the bottom of the ladder (EMR < EMT-B < AEMT < Paramedic). An EMR sees EMT-B jobs as
 *    far off; an EMR job is never far off for an EMT-B.
 *  - A person who chose "Driver / non-certified" (`driver: true`): a job that requires an EMS
 *    certification (a ladder level, NREMT or a State EMS Certification) they do not hold is far off,
 *    so a driver sees driver jobs (and jobs that ask for no EMS certification) by default.
 */
import {
  EMR_CERTIFICATION,
  STATE_EMS_CERTIFICATION,
  certificationMatchKey,
  certificationSatisfies,
  certificationSatisfiesForJob,
  normalizeStateCertificationLevel,
  profileHasCertificationForJob,
  type CertificationJobContext,
  type HeldCertification,
} from '../constants/certifications';
import { certificationJobContext, getJobCertRequirements } from '../certificationMatching';
import { jobRequiredEmsCertifications } from './driverRoles';

/** The EMS ladder, lowest first. Index + 1 is the level rank. */
export const EMS_LADDER = ['EMT-B', 'AEMT', 'Paramedic'] as const;
/** The ladder while `driver_roles` is on: EMR below EMT-B. */
export const EMS_LADDER_WITH_EMR = [EMR_CERTIFICATION, ...EMS_LADDER] as const;

/** `driverRoles`: the driver_roles flag. `driver`: the viewer chose "Driver / non-certified" (flag on). */
export type FarOffOptions = { driverRoles?: boolean; driver?: boolean };

function ladder(opts?: FarOffOptions): readonly string[] {
  return opts?.driverRoles ? EMS_LADDER_WITH_EMR : EMS_LADDER;
}

/** Ladder rank a requirement asks for (1 = the lowest level), 0 when not on the ladder. */
export function requiredLadderRank(cert: string, opts?: FarOffOptions): number {
  const key = certificationMatchKey(cert);
  const i = ladder(opts).findIndex((l) => certificationMatchKey(l) === key);
  return i + 1;
}

/** The highest level `held` reaches on its own (no job context), 0 when it is not a level credential. */
export function heldLadderRank(held: HeldCertification, opts?: FarOffOptions): number {
  if (!held?.name?.trim()) return 0;
  if (certificationMatchKey(held.name) === certificationMatchKey(STATE_EMS_CERTIFICATION)) {
    return requiredLadderRank(normalizeStateCertificationLevel(held.level) ?? 'EMT-B', opts);
  }
  let rank = 0;
  ladder(opts).forEach((level, i) => {
    if (certificationSatisfies(held.name, level)) rank = Math.max(rank, i + 1);
  });
  return rank;
}

/** The highest level the EMT reaches for this job (adds the Florida state-certification rule). */
export function emtLadderRankForJob(held: readonly HeldCertification[], ctx?: CertificationJobContext | null, opts?: FarOffOptions): number {
  let rank = 0;
  for (const h of held) {
    rank = Math.max(rank, heldLadderRank(h, opts));
    ladder(opts).forEach((level, i) => {
      if (certificationSatisfiesForJob(h.name, h.state, level, ctx, h.level)) rank = Math.max(rank, i + 1);
    });
  }
  return rank;
}

/** Highest ladder level among the job's REQUIRED certifications (0 = none). */
export function jobRequiredLadderRank(job: unknown, opts?: FarOffOptions): number {
  const { required } = getJobCertRequirements((job ?? {}) as Parameters<typeof getJobCertRequirements>[0]);
  return required.reduce((max, r) => Math.max(max, requiredLadderRank(r, opts)), 0);
}

export function isFarOffJob(job: unknown, held: readonly HeldCertification[], opts?: FarOffOptions): boolean {
  if (opts?.driverRoles && opts.driver) {
    // A driver: an EMS-certified job is far off unless they already hold what it requires.
    const ctx = certificationJobContext(job);
    return jobRequiredEmsCertifications(job).some((r) => !profileHasCertificationForJob(held, r, ctx));
  }
  const needed = jobRequiredLadderRank(job, opts);
  if (needed === 0) return false;
  const emtHasAnyLevel = held.some((h) => heldLadderRank(h, opts) > 0);
  if (!emtHasAnyLevel) return false;
  return emtLadderRankForJob(held, certificationJobContext(job), opts) < needed;
}

/** Split a list (order kept) into what the board shows by default and how many are hidden. */
export function splitFarOffJobs<T>(jobs: readonly T[], held: readonly HeldCertification[], opts?: FarOffOptions): { visible: T[]; hidden: T[] } {
  const visible: T[] = [];
  const hidden: T[] = [];
  for (const j of jobs) (isFarOffJob(j, held, opts) ? hidden : visible).push(j);
  return { visible, hidden };
}
